import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";

const MaintenanceMode = ({ children }) => {
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    checkMaintenanceMode();
  }, []);

  const checkMaintenanceMode = async () => {
    try {
      // Set a short timeout to avoid blocking page load
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
      
      const response = await axios.get(`${API}/settings/maintenance`, {
        signal: controller.signal,
        skipErrorToast: true
      });
      clearTimeout(timeoutId);
      
      const data = response.data;
      setMaintenanceEnabled(data.enabled || false);
      setMaintenanceMessage(data.message || "We're performing scheduled maintenance. We'll be back soon!");
    } catch (error) {
      // If endpoint fails or times out, assume no maintenance
      setMaintenanceEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  // Don't block admin pages
  const isAdminPage = location.pathname.startsWith("/admin");

  // Don't show loading spinner - let page render while checking
  // Only block if maintenance is actually enabled

  // Show maintenance page if enabled and not on admin page
  if (maintenanceEnabled && !isAdminPage) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-card border border-border rounded-lg shadow-lg p-12 text-center">
            {/* Icon */}
            <div className="w-24 h-24 bg-yellow-500/10 border-2 border-yellow-500/20 rounded-xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            {/* Title */}
            <h1 className="text-4xl font-heading font-bold text-foreground mb-4">Under Maintenance</h1>

            {/* Message */}
            <p className="text-xl text-muted-foreground mb-8">
              {maintenanceMessage}
            </p>

            {/* Additional Info */}
            <div className="bg-muted rounded-lg p-6 mb-8">
              <p className="text-muted-foreground text-sm">
                We're working hard to improve your experience. 
                Please check back shortly.
              </p>
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md transition"
            >
              Check Again
            </button>

            {/* Footer */}
            <p className="text-muted-foreground text-sm mt-8">
              Need urgent support? Contact us at support@cbaiq.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default MaintenanceMode;
